import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { CheckIcon } from "lucide-react"

import { cn } from "@/lib/utils"

interface CheckboxProps extends React.ComponentProps<typeof CheckboxPrimitive.Root> {
    variant?: "primary" | "secondary";
    label?: React.ReactNode; // Accepts React Node for dynamic content
    id?: string;
    hasError?: boolean;
    errorMessage?: string;
}

function Checkbox({ className, variant = "secondary", label, id, hasError, errorMessage, ...props }: CheckboxProps) {
   
    const { 'aria-label': ariaLabelFromProps, ...checkboxProps } = props;
    
    // Determine accessible name:
    // - If label exists, the label element provides the accessible name (no aria-label needed)
    // - If no label, use aria-label from props or provide a default fallback
    const accessibleName = label ? undefined : (ariaLabelFromProps || "Checkbox");
    
    return (
        <div className="relative">
            <div className="flex items-center space-x-2">
                <CheckboxPrimitive.Root
                    id={id}
                    data-slot="checkbox"
                    aria-invalid={hasError ? "true" : "false"}
                    aria-describedby={hasError && errorMessage ? id+"-error-message" : undefined}
                    aria-label={accessibleName}
                    className={cn(
                        "peer border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
                        hasError && "border-red-500 focus:border-red-500 focus:ring-red-500 ring-red-500 ring-[0.25px]",
                        variant === "primary"
                            ? "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary"
                            : "data-[state=checked]:bg-secondary data-[state=checked]:text-secondary-foreground data-[state=checked]:border-secondary",
                        className
                    )}
                    {...checkboxProps}
                >
                    <CheckboxPrimitive.Indicator
                        data-slot="checkbox-indicator"
                        className="flex items-center justify-center text-current transition-none"
                        aria-hidden="true"
                    >
                        <CheckIcon className="size-3.5" aria-hidden="true" />
                    </CheckboxPrimitive.Indicator>
                </CheckboxPrimitive.Root>
                {label && (
                    <label
                        htmlFor={id}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                        {label}
                    </label>
                )}
            </div>
            {hasError && errorMessage && (
                <p 
                    id={id+"-error-message"}
                    className="text-sm text-red-500 mt-1"
                    role="alert"
                    aria-live="polite"
                >
                    {errorMessage}
                </p>
            )}
        </div>
    )
}

export { Checkbox }
